import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SplitLines, EASE } from "./Reveal";
import { HeroSlider } from "./HeroSlider";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { BUSINESS, waLink } from "../data/business";

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(useTransform(mx, [-0.5, 0.5], [14, -14]), { stiffness: 60, damping: 20 });
  const py = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 20 });
  const { scrollY } = useScroll();
  const imgScale = useTransform(scrollY, [0, 900], [1, 1.12]);
  const textY = useTransform(scrollY, [0, 900], [0, -60]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}
      data-testid="hero-section"
      className="relative pt-[72px] lg:pt-[112px] lg:min-h-[100vh] lg:pb-10 overflow-hidden grain"
    >
      <div className="lg:mx-auto lg:max-w-[1440px] lg:px-12 xl:px-16 lg:grid lg:grid-cols-[52fr_48fr] lg:gap-12 xl:gap-16 lg:items-center lg:min-h-[calc(100vh-152px)]">
      <div className="relative w-full h-[58svh] sm:h-[64svh] lg:h-auto lg:aspect-[3/2] lg:max-h-[calc(100vh-200px)] min-h-[320px] lg:min-h-0 lg:order-2 overflow-hidden img-frame" data-testid="hero-visual">
        <motion.div
          initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0"
        >
          <HeroSlider parallaxStyle={{ x: px, y: py, scale: imgScale }} />
        </motion.div>
      </div>

      <motion.div style={{ y: textY }} className="relative lg:order-1">
        <div className="mx-auto w-full max-w-[1440px] px-5 sm:px-8 lg:px-0 py-12 sm:py-16 lg:py-0">
          <div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
              className="eyebrow mb-6 sm:mb-8" data-testid="hero-eyebrow"
            >
              Lab Grown Diamonds Hyderabad · Abids · Est. 2026 · GST 36ARYPS5502Q1ZP
            </motion.p>

            <h1 data-testid="hero-title" className="font-display font-light leading-[0.92] lg:leading-[0.9] text-[clamp(3rem,10vw,5.2rem)] lg:text-[clamp(3.5rem,4.6vw,5.25rem)] tracking-[-0.01em]">
              <SplitLines className="gold-text" lines={["Lab-Grown.", "Precision.", <em key="c" className="italic text-ivory" style={{ WebkitTextFillColor: "#F2ECDD" }}>Certified.</em>]} delay={0.35} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease: EASE }}
              className="mt-7 sm:mt-10 lg:mt-6 max-w-xl text-ivory/75 text-base sm:text-lg leading-relaxed font-light"
              data-testid="hero-ovp"
            >
              Brilliant lab-grown diamonds for every story — from the solitaire you'll wear for a lifetime to the calibrated melee parcels that keep a jeweller's bench busy.
              IGI-certified, ethically grown and sold carat-wise with full disclosure. Try stones on your hand at Abids, or send one <WhatsAppIcon size={16} className="text-[#25D366]" /> message for photos, a quote and a GST invoice.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1, ease: EASE }}
              className="mt-9 sm:mt-12 lg:mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4"
            >
              <Link to="/browse" data-testid="hero-browse-btn" className="btn-gold justify-center">
                Browse Lab Grown Diamonds <ArrowRight size={15} strokeWidth={1.8} />
              </Link>
              <a href={waLink(`Hello ${BUSINESS.name}, I'd like a quote on lab-grown diamonds.`)} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="btn-outline justify-center">
                Quote on <WhatsAppIcon size={17} className="text-[#25D366]" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7, duration: 1 }}
              className="mt-12 sm:mt-16 lg:mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-lg border-t border-gold/20 pt-6"
            >
              {[["10", "Diamond shapes"], ["IGI", "Certified stones"], ["You · Trade", "Rings to parcels"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-display text-2xl sm:text-3xl text-saffron">{v}</p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-ivory/50 mt-1">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.div>
      </div>
    </section>
  );
};
